import { useMutation } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import React, { useCallback, useState } from "react"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import ClientData from "@/common/client_data"
import FormForm from "@/common/components/form_form"
import FormInput from "@/common/components/form_input"
import { Routemap } from "@/common/routes"
import yup from "@/common/yup"
import { LogInOutline } from "@/skylticons"

const loginDoc = graphql(`
  mutation CustomerLogin($login: String!, $password: String) {
    customerLogin(login: $login, password: $password) {
      success
      adminInvalidCustomer
      redirect

      customer {
        id
        __typename
      }

      errors {
        path
        message
      }
    }
  }
`)

interface LoginFormInputs {
  [key: string]: string | undefined | null
  login: string
  password?: string
}

const loginSchema = (admin: boolean) =>
  yup.object().shape<LoginFormInputs>({
    login: yup.string().required(),
    password: admin ? yup.string() : yup.string().required(),
  })

interface LoginFormProps {
  showForgotPassword: (email?: string) => void
  toggleForgotPassword: () => void
  admin: boolean
}

const LoginForm: React.FC<LoginFormProps> = ({ toggleForgotPassword, showForgotPassword, admin }) => {
  const { t } = useTranslation()
  const [mutation] = useMutation(loginDoc)
  const formMethods = useForm<LoginFormInputs>({
    resolver: yupResolver(loginSchema(admin)),
  })
  const { handleSubmit } = formMethods

  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string>()

  const submit: SubmitHandler<LoginFormInputs> = useCallback(
    async (form, e) => {
      e?.preventDefault()

      setLoading(true)
      setMessage(undefined)
      setFailed(false)
      const result = await mutation({ variables: form })
      const data = result.data?.customerLogin

      if (data) {
        if (data.adminInvalidCustomer) {
          if (data.errors) {
            setMessage(data.errors[0].message)
          }

          setLoading(false)
          return
        }

        if (data.success) {
          location.href = Routemap.customerZone(t)
        } else {
          setLoading(false)
          setFailed(true)
          showForgotPassword(form.login.includes("@") ? form.login : "")
        }
      }
    },
    [mutation, showForgotPassword, t]
  )

  return (
    <FormForm id="login" noValidate onSubmit={handleSubmit(submit)} formMethods={formMethods}>
      {message && <p className="paragraph-sm">{message}</p>}
      {failed && <div dangerouslySetInnerHTML={{ __html: ClientData.snippet("login_failed") }} />}
      <FormInput type="email" name="login" id="login_login" label={t("customer.email_address")} />
      <FormInput type="password" name="password" id="login_password" label={t("customer.password")} />
      <p className="paragraph-sm right no-max-width forgot">
        <a id="forgot_password_link" onClick={toggleForgotPassword}>
          {t("customer.forgot_password")}
        </a>
      </p>
      <div className="center">
        <button
          id="login_submit"
          className="button button-primary button-medium full-width mb-0"
          type="submit"
          disabled={loading}
          data-gtm="login-button"
        >
          <LogInOutline />
          {t("customer.log_in")}
        </button>
      </div>
    </FormForm>
  )
}

export default LoginForm
